import type { Control } from "react-hook-form";
import { BILLING_PROFILE_LIMITS, type BillingProfileForm } from "@/shared/api/contracts";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";

/**
 * The nine fields Razorpay puts on a GST invoice (ADR-0012). Kept apart from the form's logic so the
 * container stays a container (rules/01 §9's split pattern); the limits come from the mirrored schema,
 * never typed by hand (rules/01 §5).
 */
export function BillingProfileFields({
  control,
  canManage,
}: {
  control: Control<BillingProfileForm>;
  canManage: boolean;
}) {
  return (
    <>
      <FormField
        control={control}
        name="legalName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Registered business name</FormLabel>
            <FormControl>
              <Input {...field} maxLength={BILLING_PROFILE_LIMITS.legalName} disabled={!canManage} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          control={control}
          name="addressLine1"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address</FormLabel>
              <FormControl>
                <Input {...field} maxLength={BILLING_PROFILE_LIMITS.addressLine} disabled={!canManage} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="addressLine2"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address line 2 (optional)</FormLabel>
              <FormControl>
                <Input {...field} maxLength={BILLING_PROFILE_LIMITS.addressLine} disabled={!canManage} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormLabel>City</FormLabel>
              <FormControl>
                <Input {...field} maxLength={BILLING_PROFILE_LIMITS.city} disabled={!canManage} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="state"
          render={({ field }) => (
            <FormItem>
              <FormLabel>State</FormLabel>
              <FormControl>
                <Input {...field} maxLength={BILLING_PROFILE_LIMITS.state} disabled={!canManage} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="postcode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>PIN code</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  inputMode="numeric"
                  maxLength={BILLING_PROFILE_LIMITS.postcode}
                  disabled={!canManage}
                  className="max-w-40"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="country"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Country</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  maxLength={BILLING_PROFILE_LIMITS.country}
                  disabled={!canManage}
                  className="max-w-24 uppercase"
                />
              </FormControl>
              <FormDescription>Two-letter code, for example IN.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="gstin"
          render={({ field }) => (
            <FormItem>
              <FormLabel>GSTIN (optional)</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  maxLength={BILLING_PROFILE_LIMITS.gstin}
                  disabled={!canManage}
                  className="font-mono uppercase"
                  autoComplete="off"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="billingEmail"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Billing email (optional)</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  maxLength={BILLING_PROFILE_LIMITS.billingEmail}
                  disabled={!canManage}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );
}
